import { sanitizeDeploymentSiteSlug } from "le-coffre-resources/dist/deploymentSiteCodes.js";
import { getPublicSiteCode } from "@Front/themes/siteTheme";

/** Slug de ligne public (build `NEXT_PUBLIC_SITE_CODE`), sans liste fermée. */
export type PublicSiteCode = string;

export function readPublicSiteCode(): PublicSiteCode {
	return sanitizeDeploymentSiteSlug(getPublicSiteCode());
}

type OrganizationLexicon = {
	qualifiedTitle: string;
	/** French determiner + qualified noun, used in messages (e.g. "cette étude notariale", "ce cabinet"). */
	demonstrativeTarget: string;
	/** "l'étude", "le cabinet", ... */
	subject: string;
	/** "à l'étude", "au cabinet", ... */
	toPhrase: string;
	/** "l'étude propriétaire", "le cabinet propriétaire", ... */
	ownerSubject: string;
	/** "de l'étude propriétaire", "du cabinet propriétaire", ... */
	ownerPhrase: string;
	/** "de l'étude", "du cabinet", ... */
	ofPhrase: string;
	notFoundTitle: string;
	notFoundMessage: string;
};

const LECOFFREIO_ORGANIZATION_LEXICON: OrganizationLexicon = {
	qualifiedTitle: "Étude notariale",
	demonstrativeTarget: "cette étude notariale",
	subject: "l'étude",
	toPhrase: "à l'étude",
	ownerSubject: "l'étude propriétaire",
	ownerPhrase: "de l'étude propriétaire",
	ofPhrase: "de l'étude",
	notFoundTitle: "Étude introuvable",
	notFoundMessage: "L'étude notariale demandée n'existe pas ou n'est plus active.",
};

const ORGANIZATION_LEXICONS_BY_SITE: Record<string, OrganizationLexicon> = {
	lecoffreio: LECOFFREIO_ORGANIZATION_LEXICON,
};

function organizationLexiconForSite(siteCode: string): OrganizationLexicon {
	const lex = ORGANIZATION_LEXICONS_BY_SITE[siteCode];
	if (!lex) {
		throw new Error(
			`[multi-site] No organization lexicon for public site code "${siteCode}". Expected one of: ${Object.keys(ORGANIZATION_LEXICONS_BY_SITE).join(", ")}. There is no fallback to another deployment line.`,
		);
	}
	return lex;
}

export function organizationQualifiedTitle(): string {
	return organizationLexiconForSite(readPublicSiteCode()).qualifiedTitle;
}

export function organizationDemonstrativeTarget(): string {
	return organizationLexiconForSite(readPublicSiteCode()).demonstrativeTarget;
}

export function organizationSubject(): string {
	return organizationLexiconForSite(readPublicSiteCode()).subject;
}

export function organizationOwnerPhrase(): string {
	return organizationLexiconForSite(readPublicSiteCode()).ownerPhrase;
}

export function organizationOfPhrase(): string {
	return organizationLexiconForSite(readPublicSiteCode()).ofPhrase;
}

export function organizationToPhrase(): string {
	return organizationLexiconForSite(readPublicSiteCode()).toPhrase;
}

export function organizationOwnerSubject(): string {
	return organizationLexiconForSite(readPublicSiteCode()).ownerSubject;
}

export function organizationNotFoundTitle(): string {
	return organizationLexiconForSite(readPublicSiteCode()).notFoundTitle;
}

export function organizationNotFoundMessage(): string {
	return organizationLexiconForSite(readPublicSiteCode()).notFoundMessage;
}

type WorkspaceLexicon = {
	nounTitle: string;
	nounLower: string;
	pluralTitle: string;
	/** "l'office", "le cabinet", ... */
	subject: string;
	/** "cet office", "ce cabinet", ... */
	demonstrativeTarget: string;
	/** "à l'office", "au cabinet", ... */
	toPhrase: string;
	/** "de l'office", "du cabinet", ... */
	ofPhrase: string;
	/** "Tout l'office", "Tout le cabinet", ... */
	allLabel: string;
	/** "Administrateur de l'office", "Administrateur du cabinet", ... */
	adminLabel: string;
	/** "Office inconnu", "Cabinet inconnu", ... */
	unknownLabel: string;
	/** "d'offices", "de cabinets", "de bureaux" */
	searchOfPhrase: string;
};

const LECOFFREIO_WORKSPACE_LEXICON: WorkspaceLexicon = {
	nounTitle: "Office",
	nounLower: "office",
	pluralTitle: "Offices",
	subject: "l'office",
	demonstrativeTarget: "cet office",
	toPhrase: "à l'office",
	ofPhrase: "de l'office",
	allLabel: "Tout l'office",
	adminLabel: "Administrateur de l'office",
	unknownLabel: "Office inconnu",
	searchOfPhrase: "d'offices",
};

const WORKSPACE_LEXICONS_BY_SITE: Record<string, WorkspaceLexicon> = {
	lecoffreio: LECOFFREIO_WORKSPACE_LEXICON,
};

function workspaceLexiconForSite(siteCode: string): WorkspaceLexicon {
	const lex = WORKSPACE_LEXICONS_BY_SITE[siteCode];
	if (!lex) {
		throw new Error(
			`[multi-site] No workspace lexicon for public site code "${siteCode}". Expected one of: ${Object.keys(WORKSPACE_LEXICONS_BY_SITE).join(", ")}. There is no fallback to another deployment line.`,
		);
	}
	return lex;
}

export function workspaceNounTitle(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).nounTitle;
}

export function workspaceNounLower(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).nounLower;
}

export function workspacePluralTitle(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).pluralTitle;
}

export function workspaceSubject(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).subject;
}

export function workspaceDemonstrativeTarget(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).demonstrativeTarget;
}

export function workspaceToPhrase(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).toPhrase;
}

export function workspaceOfPhrase(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).ofPhrase;
}

export function workspaceAllLabel(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).allLabel;
}

export function workspaceAdminLabel(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).adminLabel;
}

export function workspaceUnknownLabel(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).unknownLabel;
}

export function workspaceSearchOfPhrase(): string {
	return workspaceLexiconForSite(readPublicSiteCode()).searchOfPhrase;
}
